import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Download, ChevronLeft, ChevronRight, Users, Building2, MapPin, Utensils, Printer } from 'lucide-react';
import { FMBExportDialog } from '../components/FMBExportDialog';

export default function DrilldownPage() {
  const { type, value } = useParams();
  const navigate = useNavigate();
  const { api, canExportExcel } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [mohallas, setMohallas] = useState([]);
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showFMBExportDialog, setShowFMBExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf'); // Default to PDF
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);
  
  const limit = 50;

  const fetchMohallas = useCallback(async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  }, [api]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = '';
      const params = new URLSearchParams({ page, limit });

      switch (type) {
        case 'fmb':
          endpoint = `/drilldown/fmb/${value}`;
          if (selectedMohalla) params.append('mohalla', selectedMohalla);
          break;
        case 'mohalla':
          endpoint = `/drilldown/mohalla/${encodeURIComponent(value)}`;
          break;
        case 'sector':
          endpoint = `/drilldown/sector/${encodeURIComponent(value)}`;
          if (selectedMohalla) params.append('mohalla', selectedMohalla);
          break;
        case 'non-tagged':
          endpoint = `/drilldown/non-tagged/families`;
          break;
        case 'mashaikh':
          endpoint = `/drilldown/mashaikh`;
          break;
        case 'mafasih':
          endpoint = `/drilldown/mafasih`;
          break;
        default:
          toast.error('Invalid drilldown type');
          navigate('/');
          return;
      }

      const response = await api.get(`${endpoint}?${params}`);
      setData(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api, type, value, page, selectedMohalla, navigate]);

  useEffect(() => {
    fetchMohallas();
  }, [fetchMohallas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (format = 'excel') => {
    try {
      setExporting(true);
      let url = `/export/universal?export_type=${type}&format=${format}`;
      if (type === 'fmb') url += `&fmb_status=${encodeURIComponent(value)}`;
      if (type === 'sector') url += `&sector=${encodeURIComponent(value)}`;
      if (type === 'mohalla') url += `&mohalla=${encodeURIComponent(value)}`;
      if (type === 'non-tagged') url += `&non_tagged=true`;
      if (type === 'mashaikh') url = `/export/universal?export_type=mashaaikh&format=${format}`;
      if (type === 'mafasih') url = `/export/universal?export_type=mafasi&format=${format}`;
      if (selectedMohalla) url += `&mohalla=${encodeURIComponent(selectedMohalla)}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create blob
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const fileName = `${type}_${value.replace(/ /g, '_')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Mobile-friendly download
      if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
        // For mobile: open in new tab
        const blobUrl = window.URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      } else {
        // Desktop download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
      
      toast.success(`${format.toUpperCase()} file downloaded successfully`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const totalPages = Math.ceil(total / limit);

  const getIcon = () => {
    switch (type) {
      case 'fmb': return <Utensils className="w-5 h-5" />;
      case 'mohalla': return <Building2 className="w-5 h-5" />;
      case 'sector': return <MapPin className="w-5 h-5" />;
      case 'non-tagged': return <Users className="w-5 h-5" />;
      case 'mashaikh': return <Users className="w-5 h-5" />;
      case 'mafasih': return <Users className="w-5 h-5" />;
      default: return <Users className="w-5 h-5" />;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'fmb': return `FMB ${value}`;
      case 'mohalla': return `Mohalla: ${value}`;
      case 'sector': return `Sector: ${value}`;
      case 'non-tagged': return `Non-Tagged Families`;
      case 'mashaikh': return `Mashaikh`;
      case 'mafasih': return `Mafasih`;
      default: return 'Drilldown';
    }
  };

  // Sector, FMB and non-tagged views show HOF-only data
  const isHofOnlyView = type === 'sector' || type === 'fmb' || type === 'non-tagged';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="drilldown-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            {getIcon()}
            <h1 className="text-2xl font-heading font-semibold text-slate-800">{getTitle()}</h1>
            <Badge className="ml-2">{total} {isHofOnlyView ? 'families' : 'records'}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {(type === 'fmb' || type === 'sector') && (
            <Select value={selectedMohalla || "all"} onValueChange={(v) => { setSelectedMohalla(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Mohalla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => type === 'fmb' ? setShowFMBExportDialog(true) : setShowExportDialog(true)} variant="outline" data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={handlePrint} variant="outline" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Export Format Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export {getTitle()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">Choose export format:</p>
            <div className={`grid gap-4 ${canExportExcel ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {/* Excel option - Super Admin ONLY */}
              {canExportExcel && (
                <Button 
                  variant={exportFormat === 'excel' ? 'default' : 'outline'}
                  className={exportFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setExportFormat('excel')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Excel (.xlsx)
                </Button>
              )}
              <Button 
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                className={exportFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setExportFormat('pdf')}
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
            {!canExportExcel && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                📋 Excel export is restricted to Super Admin only
              </p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={() => handleExport(exportFormat)} 
              disabled={exporting}
              className="bg-navy hover:bg-navy/90"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FMB Export Dialog (multi-step wizard) */}
      {type === 'fmb' && (
        <FMBExportDialog 
          open={showFMBExportDialog} 
          onOpenChange={setShowFMBExportDialog} 
          fmbStatus={value}
        />
      )}

      {/* Print Header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-center">{getTitle()}</h1>
        <p className="text-center text-sm text-gray-600">Total: {total} {isHofOnlyView ? 'families' : 'records'}</p>
      </div>

      {/* Data Table */}
      <Card className="border-0 shadow-card overflow-hidden print:shadow-none print:border" ref={printRef}>
        <div className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Sr</TableHead>
                {isHofOnlyView ? (
                  type === 'fmb' ? (
                    // FMB drilldown - HOF-only columns matching main FMB page
                    <>
                      <TableHead>HOF ITS</TableHead>
                      <TableHead>HOF Name</TableHead>
                      <TableHead className="hidden md:table-cell">Mohalla</TableHead>
                      <TableHead>Sector</TableHead>
                      <TableHead>Thaali No</TableHead>
                      <TableHead>Status</TableHead>
                    </>
                  ) : (
                    // Sector drilldown - detailed HOF view
                    <>
                      <TableHead>HOF Name</TableHead>
                      <TableHead>HOF ITS</TableHead>
                      <TableHead>HOF Mobile</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Building Tag</TableHead>
                      <TableHead>Flat No</TableHead>
                      <TableHead>Thaali</TableHead>
                      <TableHead>FMB Status</TableHead>
                    </>
                  )
                ) : (
                  <>
                    <TableHead>Member Name</TableHead>
                    <TableHead>ITS ID</TableHead>
                    <TableHead>HOF Name</TableHead>
                    <TableHead>HOF Mobile</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Building Tag</TableHead>
                    <TableHead>Thaali</TableHead>
                    <TableHead>FMB Status</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Misaq</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={type === 'fmb' ? 7 : (isHofOnlyView ? 9 : 13)} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={type === 'fmb' ? 7 : (isHofOnlyView ? 9 : 13)} className="text-center py-8 text-slate-500">
                    No data found
                  </TableCell>
                </TableRow>
              ) : type === 'fmb' ? (
                // FMB HOF-only view - matching main FMB page columns
                data.map((row, idx) => (
                  <TableRow key={idx} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/family/${row.hof_id}`)}>
                    <TableCell className="text-sm">{row.sr_no}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{row.hof_its || '-'}</TableCell>
                    <TableCell className="font-medium text-sm">{row.hof_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{row.mohalla || '-'}</TableCell>
                    <TableCell className="text-sm">{row.sector || '-'}</TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold text-navy text-sm">
                        {row.thaali_number || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${row.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}`}>
                        {row.fmb_status || '-'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              ) : isHofOnlyView ? (
                // Sector HOF-only view - detailed
                data.map((row, idx) => (
                  <TableRow key={idx} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/family/${row.hof_id || row.sr_no}`)}>
                    <TableCell>{row.sr_no}</TableCell>
                    <TableCell className="font-medium">{row.hof_name}</TableCell>
                    <TableCell className="font-mono text-sm">{row.hof_its || '-'}</TableCell>
                    <TableCell>{row.hof_mobile || '-'}</TableCell>
                    <TableCell className="max-w-32 truncate">{row.address || '-'}</TableCell>
                    <TableCell>{row.building_tag || '-'}</TableCell>
                    <TableCell>{row.flat_number || '-'}</TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold text-navy">
                        {row.thaali_number || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={row.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}>
                        {row.fmb_status || '-'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // Member-level view for Mohalla
                data.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.sr_no}</TableCell>
                    <TableCell className="font-medium">{row.member_name}</TableCell>
                    <TableCell className="font-mono text-sm">{row.its_id || '-'}</TableCell>
                    <TableCell>{row.hof_name}</TableCell>
                    <TableCell>{row.hof_mobile}</TableCell>
                    <TableCell>{row.mohalla || '-'}</TableCell>
                    <TableCell>{row.sector || '-'}</TableCell>
                    <TableCell className="max-w-32 truncate">{row.address || '-'}</TableCell>
                    <TableCell>{row.building_tag || '-'}</TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold text-navy">
                        {row.thaali_number || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={row.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}>
                        {row.fmb_status}
                      </Badge>
                    </TableCell>
                    <TableCell>{row.gender || '-'}</TableCell>
                    <TableCell>{row.age || '-'}</TableCell>
                    <TableCell>
                      <Badge className={row.misaq_status === 'Done' ? 'badge-success' : 'badge-warning'}>
                        {row.misaq_status || 'Not Done'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t print:hidden">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:block, .print\\:block * {
            visibility: visible;
          }
          [data-testid="drilldown-page"], [data-testid="drilldown-page"] * {
            visibility: visible;
          }
          [data-testid="drilldown-page"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
